import { useState, useId } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorBox, Load, Page, Section, useApi } from '../ui/kit';

const ago = (t: string) => { const m = Math.round((Date.now() - Date.parse(t)) / 60000); return m < 60 ? `${Math.max(1, m)} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };

type Answer = { id: string; status: 'draft' | 'sent' | 'failed' | 'skipped'; body: string | null; note: string | null; sent_at: string | null; by: 'autopilot' | 'person' | null };
type Reply = { id: string; at: string; company_id: string; company: string | null; domain: string; contact: string; title: string | null; campaign_id: string; campaign: string; text: string | null;
  class: string | null; answer: Answer | null; meeting_booked: boolean };

const KIND: Record<string, { label: string; tone: string }> = {
  interested: { label: 'Interested', tone: 'good' }, question: { label: 'Question', tone: 'next' }, not_now: { label: 'Not now', tone: '' },
  wrong_person: { label: 'Wrong person', tone: '' }, unsubscribe: { label: 'Asked to stop', tone: 'bad' }, out_of_office: { label: 'Out of office', tone: '' }, other: { label: 'Other', tone: '' },
};

/** The answer to an interested reply: sent (by autopilot or a person), or a draft to edit and send. */
function ReplyAnswer({ a }: { a: Answer }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [body, setBody] = useState(a.body ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  if (a.status === 'sent') return <p className="small reply-answered">Answered{a.by === 'autopilot' ? ' by autopilot' : ''}{a.sent_at ? ` ${ago(a.sent_at)}` : ''}, with your booking link.</p>;
  if (a.status === 'skipped') return a.note ? <p className="small muted">{a.note}</p> : null;
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ status: string; note?: string }>(`/portal/replies/answers/${a.id}/send`, { method: 'POST', body: body.trim() !== (a.body ?? '').trim() ? { body } : {} });
      if (r.status !== 'sent') setErr({ message: r.note ?? 'Not sent.' });
      await qc.invalidateQueries({ queryKey: ['/portal/replies'] });
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="reply-draft">
      <p className="small muted">{a.status === 'failed' ? a.note : 'Your answer, ready to send from the mailbox that wrote to them.'} <code>{'{{booking_link}}'}</code> becomes your booking link with their name and email filled in; your address and the unsubscribe link go below.</p>
      <textarea id={`${uid}-f1`} value={body} onChange={(e) => setBody(e.target.value)} rows={5} aria-label="Answer" maxLength={4000} />
      {err ? <ErrorBox error={err} doing="send the answer" /> : null}
      <span className="actions"><button className="btn btn-primary" type="button" disabled={busy || !body.trim()} onClick={send}>{busy ? 'Sending…' : a.status === 'failed' ? 'Try again' : 'Send answer'}</button></span>
    </div>
  );
}

/** Inbox → Replies: what people answered, newest first, each linked to its account and campaign. */
export function Replies() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? ''); // the daily brief links to one reply by name
  return (
    <Page title="Replies">
      <input id={`${uid}-f2`} className="search" placeholder="Search by name, company or text" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search replies" />
      <Load<{ data: Reply[] }> path="/portal/replies">{(d) => {
        const rows = d.data.filter((r) => !q || `${r.contact} ${r.company ?? ''} ${r.domain} ${r.text ?? ''}`.toLowerCase().includes(q.toLowerCase()));
        return rows.length ? (
          <ul className="reply-list">{rows.map((r) => (
            <li key={r.id} className="reply">
              <div className="reply-head">{r.class && KIND[r.class] ? <span className={`chip ${KIND[r.class]!.tone ? `chip-${KIND[r.class]!.tone}` : ''}`}>{KIND[r.class]!.label}</span> : null}{r.meeting_booked ? <span className="chip chip-good">Meeting booked</span> : null} <b>{r.contact || 'Someone'}</b>{r.title ? <span className="muted"> · {r.title}</span> : null} <span className="muted">at <Link to={`/accounts/${r.company_id}`}>{r.company ?? r.domain}</Link></span><time dateTime={r.at}>{ago(r.at)}</time></div>
              {r.text ? <p className="reply-text">{r.text}</p> : <p className="muted small">The reply text is in Smartlead.</p>}
              <span className="muted small">Campaign: <Link to={`/campaigns/${r.campaign_id}`}>{r.campaign}</Link></span>
              {r.answer ? <ReplyAnswer a={r.answer} /> : null}
            </li>
          ))}</ul>
        ) : <div className="empty-state"><p>{q ? 'Nothing matches.' : 'No replies yet. Replies to your campaigns appear here.'}</p></div>;
      }}</Load>
    </Page>
  );
}

type Referrals = { code: string; link: string; credits_each: number; usd_each: number; yearly_cap_credits: number; earned_this_year: number; referrals: Array<{ name: string; status: string; reason: string | null; credits: number; created_at: string }> };

/** Refer and earn (as Explee): a link, what you earned against the yearly cap, who joined, and the rules. */
export function Refer() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [copied, setCopied] = useState(false);
  return (
    <Page title="Refer and earn">
      <Load<Referrals> path="/portal/referrals">{(r) => (
        <div className="refer">
          <section className="section refer-hero">
            <h2>Give ${r.usd_each}, get ${r.usd_each}</h2>
            <p className="muted">Invite founders and sales teams. When their workspace makes its first payment, they get {r.credits_each} credits (about ${r.usd_each}) and so do you.</p>
            <div className="secret-row refer-link">
              <input id={`${uid}-f3`} readOnly value={r.link} aria-label="Your referral link" onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="btn btn-primary" onClick={async () => { try { await navigator.clipboard.writeText(r.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* selectable */ } }}>{copied ? 'Copied' : 'Copy link'}</button>
            </div>
          </section>
          <Section title="Your referral earnings">
            <p><b className="big">{r.earned_this_year.toLocaleString('en-US')}</b> credits earned · {(r.yearly_cap_credits - r.earned_this_year).toLocaleString('en-US')} left this year</p>
            <div className="meter" role="img" aria-label={`${r.earned_this_year} of ${r.yearly_cap_credits} credits`}><i style={{ width: `${Math.min(100, (r.earned_this_year / Math.max(1, r.yearly_cap_credits)) * 100)}%` }} /></div>
          </Section>
          <Section title="Referrals">
            {r.referrals.length ? (
              <table className="table"><thead><tr><th>Workspace</th><th>Status</th><th>Credits</th><th>Joined</th></tr></thead>
                <tbody>{r.referrals.map((x, i) => <tr key={i}><td>{x.name}</td><td><span className={`chip ${x.status === 'paid' ? 'done' : ''}`}>{x.status}</span>{x.reason ? <span className="muted small"> {x.reason}</span> : null}</td><td>{x.credits || '—'}</td><td>{x.created_at.slice(0, 10)}</td></tr>)}</tbody></table>
            ) : <p className="muted">No referrals yet. Share your link to start earning.</p>}
          </Section>
          <Section title="The rules">
            <ul className="rules">
              <li>A referral pays out when the invited workspace makes its first real payment.</li>
              <li>You can't refer your own company: invites to your own domain or to people in your workspace don't qualify.</li>
              <li>Up to {r.yearly_cap_credits.toLocaleString('en-US')} credits a year per workspace.</li>
              <li>Self-referrals and duplicate companies are held automatically and don't pay out.</li>
            </ul>
          </Section>
        </div>
      )}</Load>
    </Page>
  );
}

type Kind = 'approvals' | 'hot_leads' | 'ops_alerts' | 'usage_alerts' | 'digest' | 'reports' | 'daily_brief' | 'receipts';
type Prefs = { channels: Partial<Record<Kind | 'login_links', boolean>>; telegram: { available: boolean; linked: boolean; alerts: Partial<Record<Kind, boolean>> } };
// each alert: in the app (always, in the thread it is about), by email and in Telegram, switched per type
// optIn: the API says whether it is on (the daily brief: your choice, else on for owners while a campaign runs or its
// first emails wait for review); the others are on until turned off
const KINDS: Array<{ k: Kind; label: string; hint: string; telegram: boolean; optIn?: boolean }> = [
  { k: 'hot_leads', label: 'Hot leads', hint: 'A reply or signal worth a call, handed to your team', telegram: true },
  { k: 'approvals', label: 'Approvals waiting for you', hint: 'An ICP, policies, the first campaign’s emails', telegram: true },
  { k: 'ops_alerts', label: 'Problems that need you', hint: 'A paused campaign, a bounce spike, a disconnected CRM', telegram: true },
  { k: 'usage_alerts', label: 'Credit alerts', hint: 'When credits or the daily budget run low', telegram: true },
  { k: 'daily_brief', label: 'Daily brief', hint: 'Once a day at your digest time: what needs you (reviews, paused campaigns, credits), then yesterday’s replies, meetings and numbers. On by default for owners while a campaign runs; no email on a day with nothing to say', telegram: false, optIn: true },
  { k: 'digest', label: 'Hot-lead digest', hint: 'New and overdue hot leads assigned to you, each morning', telegram: false },
  { k: 'reports', label: 'Weekly report', hint: 'Accounts, replies and meetings, every Monday', telegram: false },
  { k: 'receipts', label: 'Payment receipts', hint: 'A receipt for each paid invoice (owners)', telegram: false },
];

/** Notifications: per alert type, in the app (always), by email and in Telegram. */
export function Notifications() {
  const qc = useQueryClient();
  const p = useApi<Prefs>('/v1/me/notifications');
  const [err, setErr] = useState<unknown>(null);
  const [link, setLink] = useState<string | null>(null);
  const put = async (body: unknown) => {
    setErr(null);
    try { await api('/v1/me/notifications', { method: 'PUT', body }); void qc.invalidateQueries({ queryKey: ['/v1/me/notifications'] }); } catch (e) { setErr(e); }
  };
  const connect = async () => { setErr(null); try { setLink((await api<{ url: string }>('/v1/me/telegram/link', { method: 'POST' })).url); } catch (e) { setErr(e); } };
  const disconnect = async () => { setErr(null); try { await api('/v1/me/telegram', { method: 'DELETE' }); setLink(null); void qc.invalidateQueries({ queryKey: ['/v1/me/notifications'] }); } catch (e) { setErr(e); } };
  const tg = p.data?.telegram;
  const Switch = ({ on, label, onClick, disabled }: { on: boolean; label: string; onClick: () => void; disabled?: boolean }) => (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className="switch" onClick={onClick} disabled={disabled}><span /></button>
  );
  return (
    <Page title="Notifications">
      <Section title="Alerts">
        <p className="muted">Every alert also appears in the app, in the conversation it is about (a campaign’s, or your workspace’s), so nothing is lost when you are away.</p>
        {p.data ? (
          <div className="table-wrap">
            <table className="table notif-grid">
              <thead><tr><th>Alert</th><th>In the app</th><th>Email</th><th>Telegram</th></tr></thead>
              <tbody>{KINDS.map(({ k, label, hint, telegram, optIn }) => {
                const on = optIn ? p.data!.channels[k] === true : p.data!.channels[k] !== false;
                return <tr key={k}>
                  <td className="notif-what"><b>{label}</b><br /><small className="muted">{hint}</small></td>
                  <td data-label="In the app">{telegram ? <span className="chip done" title="Always on">Always</span> : <span className="muted">—</span>}</td>
                  <td data-label="Email"><Switch on={on} label={`${label} by email`} onClick={() => void put({ channels: { [k]: !on } })} /></td>
                  <td data-label="Telegram">{telegram && tg?.linked ? <Switch on={tg.alerts[k] !== false} label={`${label} in Telegram`} onClick={() => void put({ telegram: { [k]: tg.alerts[k] === false } })} /> : <span className="muted">—</span>}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        ) : p.error ? <ErrorBox error={p.error} /> : <p className="muted" role="status">Loading…</p>}
      </Section>
      {/* when the daily brief and digests arrive: here with the rest (it was a second "Notifications" tab in Settings) */}
      <Section title="When the daily brief arrives">
        {p.data ? <BriefTime time={String((p.data as unknown as { digest_time?: string }).digest_time ?? '').slice(0, 5)} zone={String((p.data as unknown as { timezone?: string }).timezone ?? '')} save={(b) => put(b)} /> : null}
      </Section>
      <Section title="Sign in from email links">
        {p.data ? (
          <p className="notif-login">
            <Switch on={p.data.channels.login_links !== false} label="Links in emails sign me in" onClick={() => void put({ channels: { login_links: p.data!.channels.login_links === false } })} />
            {' '}<span>Links in Outlevo’s emails sign you in. Each link works once, for 72 hours, and only while you are a member of this workspace; it never counts as your second factor, so approvals still ask for it. Off: links open the page and ask you to sign in.</span>
          </p>
        ) : null}
      </Section>
      <Section title="Telegram">
        {!tg ? null : !tg.available ? <p className="muted">Telegram alerts are coming soon.</p>
          : tg.linked ? <p>Your Telegram is connected. <button type="button" className="link" onClick={() => void disconnect()}>Disconnect</button></p>
          : link ? <p>Open <a href={link} target="_blank" rel="noreferrer">this link</a> in Telegram and press Start. Then come back here. <button type="button" className="link" onClick={() => void qc.invalidateQueries({ queryKey: ['/v1/me/notifications'] })}>I did it</button></p>
          : <p><button type="button" className="btn btn-primary" onClick={() => void connect()}>Connect Telegram</button> <span className="muted small">Get hot leads and problems on your phone.</span></p>}
        {err ? <ErrorBox error={err} /> : null}
      </Section>
    </Page>
  );
}

/** The time of the daily brief and digests, in the person's time zone. */
function BriefTime({ time, zone, save }: { time: string; zone: string; save: (b: { digest_time: string; timezone: string }) => Promise<void> }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [t, setT] = useState(time || '08:00');
  const [z, setZ] = useState(zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saved, setSaved] = useState(false);
  return (
    <form className="row" onSubmit={(e) => { e.preventDefault(); setSaved(false); void save({ digest_time: t, timezone: z }).then(() => setSaved(true)); }}>
      <label htmlFor={`${uid}-t`}>Time</label><input id={`${uid}-t`} type="time" value={t} onChange={(e) => setT(e.target.value)} />
      <label htmlFor={`${uid}-z`}>Time zone</label><input id={`${uid}-z`} value={z} onChange={(e) => setZ(e.target.value)} placeholder="Europe/Warsaw" />
      <button type="submit" className="btn btn-sm">Save</button>{saved ? <span className="muted small" role="status">Saved</span> : null}
    </form>
  );
}
